import { Field } from "@progress/kendo-react-form";
import {
  FormCheckbox,
  FormDropDownList,
  FormNumericTextBox,
  FormRadioGroup,
  FormUpload,
} from "../../../../forms/form-components";
import {
  dataRealMockList,
  getExcelSheetsFromFile,
  maxSize,
  setFormValue,
  setProcessing,
  setSheets,
} from "../../../../redux/slices/createdatasetpageslice";
import { useDispatch, useSelector } from "react-redux";
import { FormRegion } from "../../../../components/formregion";
import { Button } from "@progress/kendo-react-buttons";
import { Fragment, useCallback, useEffect, useState } from "react";
import { addNotification } from "../../../../redux/slices/notificationsslice";
import { notificationDurationMiddle } from "../../../../lib/utils";
import { apiErrorsHandler, getErrorMessages } from "../../../../api/httpclient";
import { useNavigate } from "react-router-dom";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

const CreateFromFileStep = (props) => {
  //#region state
  const { formValues } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pageState = useSelector((state) => state.createDatasetPage);
  const appearance = useSelector((state) => state.appearance);
  const sheets = pageState.sheets;
  const IsCheckedImportData = pageState.formValues.IsCheckedImportData;
  const currentUser = useSelector((state) => state.currentUser);
  const [localFile, setLocalFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const allowedExtensions = [".xls", ".xlsx", ".csv"];
  const { t } = useTranslation();

  const dataRealMockList = [
    { label: t("datasets.create.realdata"), value: true },
    { label: t("datasets.create.mockdata"), value: false },
  ];

  //#endregion

  //#region functions
  const handleOnChangeField = useCallback(
    (id, value) => {
      dispatch(setFormValue({ id, value }));
    },
    [dispatch]
  );

  const getListOfSheets = async (file) => {
    dispatch(getExcelSheetsFromFile(file, currentUser, t)).catch((err) => {
      dispatch(setProcessing({ processing: false }));
      const errorHandler = (messages) => {
        const message = messages?.[0] || t("datasets.create.unknown_error");
        dispatch(
          addNotification({
            text: message,
            type: "error",
            closable: true,
            duration: notificationDurationMiddle,
            position: "topRight",
          })
        );
      };
      apiErrorsHandler({}, dispatch, navigate, err, "/", {
        responseReceived: errorHandler,
        unhandled: errorHandler,
      });
    });
  };

  const handleOnFileChange = useCallback(
    (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > maxSize) {
        dispatch(
          addNotification({
            text: t("datasets.create.size_error"),
            type: "error",
            closable: true,
            duration: notificationDurationMiddle,
            position: "topRight",
          })
        );
        return;
      }
      setLocalFile(file);
      dispatch(setFormValue({ id: "File", value: file }));
      dispatch(setFormValue({ id: "Filename", value: file.name }));
      dispatch(setSheets([]));
    },
    [dispatch]
  );

  function handleUploadExcelFile(e) {
    e.preventDefault();
    if (localFile !== null && pageState.formValues.Filename !== "") {
      const formData = new FormData();
      formData.append("file", localFile);
      getListOfSheets(formData);
    }
  }

  function handleOnSheetChange(e) {
    handleOnChangeField("SelectedSheet", e.value);
    handleOnChangeField("StartingRow", 2);
    handleOnChangeField("EndingRow", undefined);
    handleOnChangeField("IsDataReal", true);
  }

  function handleOnChangeImportData(e) {
    handleOnChangeField("IsCheckedImportData", e.value);
    if (e.value === false) {
      handleOnChangeField("StartingRow", 2);
      handleOnChangeField("EndingRow", undefined);
    }
  }

  function handleOnChangeStartingRow(e) {
    handleOnChangeField("StartingRow", e.value);
  }

  function handleOnChangeEndingRow(e) {
    handleOnChangeField("EndingRow", e.value);
  }

  function handleOnChangeIsDataReal(e) {
    handleOnChangeField("IsDataReal", e.value);
  }

  const isExcelFile = (file) => {
    const fileName = file.name.toLowerCase();
    return allowedExtensions.some((ext) => fileName.endsWith(ext));
  };

  useEffect(() => {
    const preventDefault = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener("dragover", preventDefault);
    document.addEventListener("drop", preventDefault);

    return () => {
      document.removeEventListener("dragover", preventDefault);
      document.removeEventListener("drop", preventDefault);
    };
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    if (e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];

      if (!isExcelFile(file)) {
        dispatch(
          addNotification({
            text: t("datasets.create.file_error"),
            type: "error",
            closable: true,
            duration: notificationDurationMiddle,
            position: "topRight",
          })
        );
        return;
      }

      const event = { target: { name: "File", files: [file] } };
      handleOnFileChange(event); // Simulate file input change event
    }
  };
  //#endregion

  return (
    <FormRegion>
      <div className="form-group form-group-1">
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <div className="k-form-field">
              <label id="File_label" htmlFor="File" className="k-label">
                {t("datasets.create.file")}
              </label>
              <div
                className={`k-widget k-upload upload-file ${
                  dragging ? "dragging" : ""
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="k-dropzone">
                  <div
                    role="button"
                    className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base k-upload-button k-focus"
                    aria-label="Select files..."
                    aria-labelledby="File_label"
                    aria-describedby=" "
                  >
                    <input
                      autoComplete="off"
                      id="File"
                      name="File"
                      accept=".csv, .xlsx, .xls"
                      type="file"
                      title={t("datasets.create.selectfile")}
                      onChange={handleOnFileChange}
                    />
                    <span>{t("datasets.create.selectfile2")}</span>
                  </div>
                  <div className="k-dropzone-hint">
                    {t("datasets.create.drop")}
                  </div>
                  {pageState.formValues.File && (
                    <ul
                      className="k-upload-files k-reset"
                      style={{
                        width: "100%",
                        position: "absolute",
                        top: "46px",
                        marginLeft: "-10px",
                      }}
                    >
                      <li
                        className="k-file"
                        data-uid={pageState.formValues.FileId}
                        tabIndex="-1"
                      >
                        <div className="k-file-single">
                          <div
                            className="k-progressbar"
                            style={{
                              transition: "opacity 1s ease-in-out",
                              opacity: "1",
                            }}
                          >
                            <span
                              className="k-progress"
                              style={{ width: "0%" }}
                            ></span>
                          </div>
                          <span className="k-file-group-wrapper">
                            <span className="k-file-group k-icon k-i-file-data"></span>
                            <span className="k-file-state"></span>
                          </span>
                          <span
                            className="k-file-name-size-wrapper"
                            style={{ textAlign: "left" }}
                          >
                            <span
                              className="k-file-name"
                              title={pageState.formValues.Filename}
                            >
                              {pageState.formValues.Filename}
                            </span>
                          </span>
                        </div>
                      </li>
                    </ul>
                  )}
                </div>
              </div>
            </div>
            {pageState.formValues.File != null && (
              <Button
                onClick={handleUploadExcelFile}
                className={"upload-button"}
              >
                {t("datasets.create.upload")}
              </Button>
            )}
          </div>
        </div>
        {pageState.formValues.File != null && sheets.length != 0 && (
          <div style={{ marginTop: "70px" }}>
            <Field
              id={"SelectedSheet"}
              name={"SelectedSheet"}
              label={t("datasets.create.sheets")}
              component={FormDropDownList}
              onChange={handleOnSheetChange}
              data={sheets}
            />
            <Field
              id={"IsCheckedImportData"}
              name={"IsCheckedImportData"}
              label={t("datasets.create.import")}
              component={FormCheckbox}
              onChange={handleOnChangeImportData}
              className="import-checkbox"
              checked={formValues.IsCheckedImportData}
            />
            {IsCheckedImportData && (
              <Fragment>
                <div className="rows-container">
                  <Field
                    id={"StartingRow"}
                    name={"StartingRow"}
                    label={t("datasets.create.start")}
                    component={FormNumericTextBox}
                    onChange={handleOnChangeStartingRow}
                  />
                  <Field
                    id={"EndingRow"}
                    name={"EndingRow"}
                    label={t("datasets.create.end")}
                    component={FormNumericTextBox}
                    onChange={handleOnChangeEndingRow}
                  />
                </div>
                <div className="rows-container">
                  <Field
                    id={"IsDataReal"}
                    name={"IsDataReal"}
                    component={FormRadioGroup}
                    data={dataRealMockList}
                    value={pageState.formValues.IsDataReal}
                    onChange={handleOnChangeIsDataReal}
                  />
                </div>
                <div style={{ width: "100%", marginBottom: "50px" }}>
                  {t("datasets.create.row1")}
                  <br />
                  {t("datasets.create.row2")}
                  <br />
                  {t("datasets.create.row3")}
                  <br />
                  {t("datasets.create.row4")}
                </div>
              </Fragment>
            )}
          </div>
        )}
      </div>
      <style>
        {`        
            .datasets-page > .content .form-root .k-form .k-form-field{
              width:100%;
            } 
            .datasets-page .form-group .upload-button{    
              width: 15%;
              margin-left: 2%;
              height: 48px;
              margin-top: 25px;
            }
            .datasets-page .form-group .rows-container{
              display: flex; 
              justify-content:space-between ; 
              margin-top:20px;
              margin-bottom:50px;
            }
            .datasets-page .form-group .rows-container .k-form-field{
              width:48% !important;
            } 
            .datasets-page .form-group .rows-container .k-form-field:nth-child(2) {
                margin-top: 4px !important;
            }
            .datasets-page .form-group .rows-container .k-radio-list{
              display: flex; 
              flex-direction: row;
              justify-content:space-between ; 
              margin-top:0;
              margin-bottom:0;
            } 
            .datasets-page .form-group .import-checkbox{
              margin-bottom:20px;
              display:block;
              float:${appearance.isRightToLeft ? "right" : "left"};
            }
          `}
      </style>
    </FormRegion>
  );
};

export default CreateFromFileStep;
